<template>
  <nav v-if="headers.length" class="on-this-page">
    <h4 class="title">On this page</h4>
    <ul class="outline-list">
      <li
        v-for="header in headers"
        :key="header.link"
        :class="['outline-item', `level-${header.level}`]"
      >
        <a :href="header.link" class="outline-link">
          {{ header.title }}
        </a>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { useData } from "vitepress";
import { computed } from "vue";

interface Header {
  level: number;
  title: string;
  slug: string;
  link: string;
}

interface RawHeader {
  level: number;
  title: string;
  slug: string;
  children?: RawHeader[];
}

const { page } = useData();

const headers = computed(() => {
  // Flatten headers and filter to h2 and h3 only
  const flatHeaders: Header[] = [];

  const flatten = (items: RawHeader[]) => {
    for (const item of items) {
      if (item.level >= 2 && item.level <= 3) {
        flatHeaders.push({
          level: item.level,
          title: item.title,
          slug: item.slug,
          link: `#${item.slug}`,
        });
      }
      if (item.children?.length) {
        flatten(item.children);
      }
    }
  };

  flatten(page.value.headers || []);
  return flatHeaders;
});
</script>

<style scoped lang="scss">
.on-this-page {
  position: sticky;
  top: 6rem;
  max-height: calc(100vh - 8rem);
  overflow-y: auto;
  padding: 1rem 0;
}

.title {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text-muted);
  margin: 0 0 1rem 0;
}

.outline-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.outline-item {
  margin: 0;
  padding: 0;

  &.level-3 {
    padding-left: 1rem;
  }
}

.outline-link {
  display: block;
  padding: 0.375rem 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  text-decoration: none;
  line-height: 1.4;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-text-base);
  }
}
</style>
